export const NODE_TYPES = {
  service:  { label: 'Service',     color: '#6366f1', inputs: ['Request'],  outputs: ['Response'], icon: 'service'  },
  database: { label: 'Database',    color: '#10b981', inputs: ['Query'],    outputs: ['Result'],   icon: 'database' },
  api:      { label: 'API Gateway', color: '#f59e0b', inputs: ['Call'],     outputs: ['Reply'],    icon: 'api'      },
  person:   { label: 'Person',      color: '#ec4899', inputs: ['Request'],  outputs: ['Action'],   icon: 'person'   },
  system:   { label: 'System',      color: '#8b5cf6', inputs: ['Request'],  outputs: ['Response'], icon: 'system'   },
  queue:    { label: 'Queue',       color: '#06b6d4', inputs: ['Publish'],  outputs: ['Consume'],  icon: 'queue'    },
  group:    { label: 'Group',       color: '#64748b', inputs: [],           outputs: [],           icon: 'group'    },
  decision: { label: 'Decision',    color: '#f97316', inputs: ['Input'],    outputs: ['Output'],   icon: 'decision' },
  diamond:  { label: 'Diamond',     color: '#f97316', inputs: ['Input'],    outputs: ['Output'],   icon: 'decision' },
  external: { label: 'External',    color: '#94a3b8', inputs: ['Call'],     outputs: ['Response'], icon: 'external' },
  logic:    { label: 'Logic',       color: '#a78bfa', inputs: ['Input'],    outputs: ['Output'],   icon: 'logic'    },
  waypoint: { label: 'Waypoint',    color: '#64748b', inputs: ['In'],       outputs: ['Out'],      icon: 'waypoint' },
  box:      { label: 'Box',         color: '#94a3b8', inputs: ['In'],        outputs: ['Out'],      icon: 'box'      },
  // ── Drawing / markup (not architecture catalog objects) ─────────────
  text:     { label: 'Text',        color: '#e2e8f0', inputs: [],           outputs: [],           icon: 'text'     },
  note:     { label: 'Note',        color: '#f59e0b', inputs: [],           outputs: [],           icon: 'note'     },
  arrow:    { label: 'Arrow',       color: '#94a3b8', inputs: [],           outputs: [],           icon: 'arrow'    },
  line:     { label: 'Line',        color: '#94a3b8', inputs: [],           outputs: [],           icon: 'line'     },
  // ── General shapes (extra ones via Settings → General Shapes) ───────
  oval:     { label: 'Oval',        color: '#10b981', inputs: ['In'],       outputs: ['Out'],      icon: 'oval'     },
  rounded:  { label: 'Rounded',     color: '#6366f1', inputs: ['In'],       outputs: ['Out'],      icon: 'rounded'  },
  cylinder: { label: 'Cylinder',    color: '#10b981', inputs: ['In'],       outputs: ['Out'],      icon: 'cylinder' },
  hexagon:  { label: 'Hexagon',     color: '#f59e0b', inputs: ['In'],       outputs: ['Out'],      icon: 'hexagon'  },
  triangle: { label: 'Triangle',    color: '#8b5cf6', inputs: ['In'],       outputs: ['Out'],      icon: 'triangle' },
  cross:    { label: 'Cross',       color: '#ec4899', inputs: ['In'],       outputs: ['Out'],      icon: 'cross'    },
  // ── Network kit ────────────────────────────────────────────────────
  net_cloud:    { label: 'Cloud',      color: '#38bdf8', inputs: ['In'], outputs: ['Out'], icon: 'net_cloud'    },
  net_subnet:   { label: 'Subnet',     color: '#818cf8', inputs: ['In'], outputs: ['Out'], icon: 'net_subnet'   },
  net_host:     { label: 'Host',       color: '#94a3b8', inputs: ['In'], outputs: ['Out'], icon: 'net_host'     },
  net_firewall: { label: 'Firewall',   color: '#f97316', inputs: ['In'], outputs: ['Out'], icon: 'net_firewall' },
  net_router:   { label: 'Router',     color: '#22d3ee', inputs: ['In'], outputs: ['Out'], icon: 'net_router'   },
  net_lb:       { label: 'Load Bal.',  color: '#a78bfa', inputs: ['In'], outputs: ['Out'], icon: 'net_lb'       },
  net_switch:   { label: 'Switch',     color: '#34d399', inputs: ['In'], outputs: ['Out'], icon: 'net_switch'   },
  // ── Pipeline kit ───────────────────────────────────────────────────
  pipe_source:  { label: 'Source',     color: '#34d399', inputs: ['In'], outputs: ['Out'], icon: 'pipe_source'  },
  pipe_process: { label: 'Process',    color: '#6366f1', inputs: ['In'], outputs: ['Out'], icon: 'pipe_process' },
  pipe_store:   { label: 'Store',      color: '#10b981', inputs: ['In'], outputs: ['Out'], icon: 'pipe_store'   },
  pipe_sink:    { label: 'Sink',       color: '#f59e0b', inputs: ['In'], outputs: ['Out'], icon: 'pipe_sink'    },
  pipe_queue:   { label: 'Queue',      color: '#06b6d4', inputs: ['In'], outputs: ['Out'], icon: 'pipe_queue'   },
  // ── UML kit ────────────────────────────────────────────────────────
  uml_class:     { label: 'Class',       color: '#818cf8', inputs: ['In'], outputs: ['Out'], icon: 'uml_class'     },
  uml_interface: { label: 'Interface',   color: '#22d3ee', inputs: ['In'], outputs: ['Out'], icon: 'uml_interface' },
  uml_enum:      { label: 'Enum',        color: '#c084fc', inputs: ['In'], outputs: ['Out'], icon: 'uml_enum'      },
  uml_actor:     { label: 'Actor',       color: '#e2e8f0', inputs: ['In'], outputs: ['Out'], icon: 'uml_actor'     },
  uml_package:   { label: 'Package',     color: '#94a3b8', inputs: ['In'], outputs: ['Out'], icon: 'uml_package'   },
} as const;

export type NodeTypeName = keyof typeof NODE_TYPES;

/** Typed annotations and freeform strokes — no sockets, not catalog objects. */
export const MARKUP_TYPES = new Set<string>(['text', 'note', 'arrow', 'line']);

/** Always shown in the Draw palette section. */
export const CORE_DRAWING_SHAPE_TYPES = new Set<string>([
  'oval', 'rounded', 'diamond', 'cylinder', 'hexagon', 'triangle',
]);

/** Extra geometric shapes gated by Settings → General Shapes. */
export const EXTRA_SHAPE_TYPES = new Set<string>(['cross']);

/** Geometric drawing shapes with four-sided sockets, magnetic edges, and rotation. */
export const GENERAL_SHAPE_TYPES = new Set<string>([
  ...CORE_DRAWING_SHAPE_TYPES,
  ...EXTRA_SHAPE_TYPES,
]);

export function isMarkupType(type: string): boolean {
  return MARKUP_TYPES.has(type);
}

/**
 * Generic sketch shapes: unlabeled boxes/ovals/etc. on the flow canvas.
 * Four-sided sockets + magnetic edge connections, not architecture chrome.
 */
export const SKETCH_SHAPE_TYPES = new Set<string>(['box', ...GENERAL_SHAPE_TYPES]);

export function isSketchShape(type: string): boolean {
  return SKETCH_SHAPE_TYPES.has(type);
}

export const NETWORK_NODE_TYPES = [
  'net_cloud', 'net_subnet', 'net_host', 'net_firewall', 'net_router', 'net_lb', 'net_switch',
] as const;

export const PIPELINE_NODE_TYPES = [
  'pipe_source', 'pipe_process', 'pipe_store', 'pipe_sink', 'pipe_queue',
] as const;

export const UML_NODE_TYPES = [
  'uml_class', 'uml_interface', 'uml_enum', 'uml_actor', 'uml_package',
] as const;

export const NETWORK_TYPE_SET = new Set<string>(NETWORK_NODE_TYPES);
export const PIPELINE_TYPE_SET = new Set<string>(PIPELINE_NODE_TYPES);
export const UML_TYPE_SET = new Set<string>(UML_NODE_TYPES);

export const UML_CLASSIFIER_TYPES = new Set<string>(['uml_class', 'uml_interface', 'uml_enum']);

/** Kit shapes that render as custom bodies (not architecture chrome). */
export const KIT_SHAPE_TYPES = new Set<string>([
  ...NETWORK_NODE_TYPES,
  ...PIPELINE_NODE_TYPES,
  ...UML_NODE_TYPES,
]);

export function isKitShape(type: string): boolean {
  return KIT_SHAPE_TYPES.has(type);
}

/**
 * Four-sided magnetic nodes: sketch + network/pipeline/UML.
 * Drag from a rim, drop on a body — no socket hunting.
 */
export const MAGNETIC_NODE_TYPES = new Set<string>([...SKETCH_SHAPE_TYPES, ...KIT_SHAPE_TYPES]);

export function isMagneticNode(type: string): boolean {
  return MAGNETIC_NODE_TYPES.has(type);
}

export const SKETCH_BOX = {
  DEFAULT_WIDTH: 160,
  DEFAULT_HEIGHT: 90,
  MIN_WIDTH: 72,
  MIN_HEIGHT: 48,
} as const;

/** Shared min size for stamp, handle, and `handleNodeResize` so the resizer cannot drift past the box. */
export function magneticMinSize(type: string): { width: number; height: number } {
  if (type === 'uml_actor') return { width: 56, height: 80 };
  if (isSketchShape(type)) return { width: SKETCH_BOX.MIN_WIDTH, height: SKETCH_BOX.MIN_HEIGHT };
  if (isMagneticNode(type)) return { width: 140, height: 80 };
  return { width: 140, height: 80 };
}

/** Circle icons stay 1:1 even without Shift (a router is a circle, not an oval). */
export function locksEqualAspect(type: string): boolean {
  return type === 'net_router';
}
